import { getProethicaModuleNumber } from "@constants/demo/proethica-tests";
import { useProethicaNextModule } from "@features/dashboard/hooks/useProethicaNextModule";
import {
	buildProethicaModuleContext,
	buildReportStatsById,
} from "@features/report/helpers/proethica-module-context";
import {
	resolveProethicaNextEntryMeta,
	resolveProethicaReportNextStep,
} from "@features/report/helpers/proethica-next-entry";
import { buildProethicaStatTiles } from "@features/report/helpers/proethica-report-stats";
import { useProethicaModuleRetake } from "@features/report/hooks/useProethicaModuleRetake";
import { useProethicaReportGeneration } from "@features/report/hooks/useProethicaReportGeneration";
import { useReviewSession, useSessionStore } from "@features/session";
import { useProethicaSessionTimeStore } from "@features/session/hooks/useProethicaSessionTimeStore";
import type { MixedItemTag } from "@memorang/types";
import { Box, useDevice } from "@memorang/ui";
import { router } from "expo-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProethicaReportFooter } from "../ProethicaReportFooter";
import { ProethicaReportShell } from "../ProethicaReportShell";
import { InsightStrips, type SessionAnalysisProps } from "./InsightStrips";
import {
	CoveredPanel,
	LearnedPanel,
	type OutlineSection,
} from "./ModulePanels";
import { NextEntryCard } from "./NextEntryCard";
import { ReportHeroCard } from "./ReportHeroCard";
import { ReportStatTiles } from "./ReportStatTiles";

/* -----------------Types--------------- */
type ModuleLandingData = {
	module?: {
		label?: string;
		title?: string;
		badgeSource?: string | number | { uri: string };
		durationLabel?: string;
		activityCount?: number;
		knowledgeCheckQuestionCount?: number;
	};
	lesson?: { heading: string; text: string };
	outline?: { heading: string; sections: OutlineSection[] };
	learningObjectives?: { heading: string; intro: string; items: string[] };
};

type Props = {
	sessionTitle: string;
	sessionAnalysisProps: SessionAnalysisProps;
};

/* -----------------Helpers--------------- */
const findModuleLanding = (
	// biome-ignore lint/suspicious/noExplicitAny: demo session items are loosely typed
	items: any[],
): ModuleLandingData | undefined => {
	const landingItem = items?.find(
		(item) => item?.metadata?.taskType === "MODULE_LANDING",
	);
	return landingItem?.content?.moduleLanding as ModuleLandingData | undefined;
};

const pluralize = (count: number, singular: string, plural: string) =>
	count === 1 ? singular : plural;

const NUMBER_WORDS: Record<string, number> = {
	one: 1,
	two: 2,
	three: 3,
	four: 4,
	five: 5,
	six: 6,
	seven: 7,
	eight: 8,
	nine: 9,
	ten: 10,
};

const toCount = (raw?: string): number | undefined => {
	if (!raw) {
		return undefined;
	}
	const lower = raw.toLowerCase();
	if (NUMBER_WORDS[lower] != null) {
		return NUMBER_WORDS[lower];
	}
	const parsed = Number.parseInt(lower, 10);
	return Number.isNaN(parsed) ? undefined : parsed;
};

const matchCount = (text: string, regex: RegExp): number | undefined =>
	toCount(text.match(regex)?.[1]);

const buildSessionSummary = (
	scenarioCount: number,
	activityCount?: number,
	knowledgeCheckCount?: number,
): string => {
	const parts: string[] = [];
	if (scenarioCount > 0) {
		parts.push(
			`${scenarioCount} ${pluralize(scenarioCount, "scenario", "scenarios")}`,
		);
	}
	if (activityCount) {
		parts.push(
			`${activityCount} ${pluralize(activityCount, "activity", "activities")}`,
		);
	}
	if (knowledgeCheckCount) {
		parts.push(`a ${knowledgeCheckCount}-question knowledge check`);
	}
	if (!parts.length) {
		return "";
	}
	const joined =
		parts.length > 1
			? `${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}`
			: parts[0];
	return `You completed ${joined} in this session.`;
};

/* -----------------Main--------------- */
const ProethicaReportView = ({ sessionTitle, sessionAnalysisProps }: Props) => {
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const currentModuleId = useSessionStore((state) => state.moduleId);
	const sessionItems = useSessionStore((state) => state.sessionItems);
	const itemStates = useSessionStore((state) => state.itemStates);
	const moduleSummary = useSessionStore((state) => state.moduleSummary);
	const sessionId = useSessionStore((state) => state.id);
	const sessionNumericId = useSessionStore((state) => state.numericId);
	const wallClockSeconds = useSessionStore((state) => state.timeElapsed);
	const persistedWallClockSeconds = useProethicaSessionTimeStore((state) =>
		state.getSessionElapsed(sessionId, sessionNumericId),
	);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const { handleReviewModule, isPreparingReview } = useReviewSession();
	const {
		nextEntry,
		isResolvingPath,
		continueLabel,
		handleContinueToNextModule,
		isContinueLoading,
	} = useProethicaNextModule(currentModuleId);
	const { handleRetakeModule, isRetaking } =
		useProethicaModuleRetake(currentModuleId);
	// Holds the per-item score/timeTaken behind both the stat tiles and the
	// analysis context.
	const { reportsReady, reportStatsLoading, report } =
		useProethicaReportGeneration();

	const moduleLanding = useMemo(
		() => findModuleLanding(sessionItems),
		[sessionItems],
	);

	const title = moduleLanding?.module?.title ?? sessionTitle;
	const objectives = moduleLanding?.learningObjectives?.items ?? [];
	const sections = moduleLanding?.outline?.sections ?? [];
	const lessonText = moduleLanding?.lesson?.text ?? "";

	const moduleNumber = getProethicaModuleNumber({
		label: moduleLanding?.module?.label,
		title: moduleLanding?.module?.title,
	} as MixedItemTag);
	// Module 1 is the content-only intro (no scored/AI work), so it has no
	// personalized feedback to surface.
	const isFirstModule = moduleNumber === 1;

	const scenarioCount = sections.filter(
		(section) => section.items.length > 0,
	).length;
	const activityCount =
		moduleLanding?.module?.activityCount ??
		matchCount(lessonText, /(\w+)\s+activit/i);
	const knowledgeCheckCount =
		moduleLanding?.module?.knowledgeCheckQuestionCount ??
		matchCount(lessonText, /(\w+)[-\s]question/i);
	const summaryText = buildSessionSummary(
		scenarioCount,
		activityCount,
		knowledgeCheckCount,
	);

	const statsById = useMemo(
		() => buildReportStatsById(report?.tagDetails),
		[report],
	);

	const additionalContext = useMemo(
		() =>
			isFirstModule
				? undefined
				: buildProethicaModuleContext(sessionItems, moduleSummary, {
						statsById,
						itemStates,
					}),
		[isFirstModule, sessionItems, moduleSummary, statsById, itemStates],
	);

	const statTiles = useMemo(
		() =>
			buildProethicaStatTiles({
				moduleNumber,
				activityCount,
				sessionItems,
				statsById,
				loading: reportStatsLoading,
				wallClockSeconds,
				persistedWallClockSeconds,
			}),
		[
			moduleNumber,
			activityCount,
			sessionItems,
			statsById,
			reportStatsLoading,
			wallClockSeconds,
			persistedWallClockSeconds,
		],
	);

	const nextStep = resolveProethicaReportNextStep(nextEntry, isResolvingPath);
	const allComplete = nextStep.kind === "home";
	const panelStyle = { flex: isMobile ? undefined : 1 };

	const handleGoHome = () => {
		resetSessionStore();
		router.replace("/(protected)/(tabs)/home");
	};

	return (
		<ProethicaReportShell
			footer={
				<ProethicaReportFooter
					retakeCta={
						currentModuleId
							? {
									label: t("report.proethica.footer.retake"),
									icon: "restore",
									loading: isRetaking,
									onPress: handleRetakeModule,
								}
							: undefined
					}
					handleGoHome={handleGoHome}
					goHomePrimary={allComplete}
				/>
			}
		>
			<ReportHeroCard
				moduleLabel={moduleLanding?.module?.label}
				badgeSource={moduleLanding?.module?.badgeSource}
				title={title}
				summaryText={summaryText}
			/>

			<ReportStatTiles tiles={statTiles} />

			{allComplete ? null : (
				<NextEntryCard
					meta={
						nextStep.kind === "entry"
							? resolveProethicaNextEntryMeta(nextStep.entry)
							: undefined
					}
					continueLabel={continueLabel}
					isContinueLoading={isContinueLoading}
					handleContinue={handleContinueToNextModule}
				/>
			)}

			<InsightStrips
				isFirstModule={isFirstModule}
				moduleSummary={moduleSummary}
				sessionAnalysisProps={sessionAnalysisProps}
				additionalContext={additionalContext}
				reportsReady={reportsReady}
			/>

			<Box
				flexDirection={isMobile ? "column" : "row"}
				alignItems="stretch"
				style={{ gap: 8 }}
			>
				<LearnedPanel objectives={objectives} style={panelStyle} />
				<CoveredPanel
					sections={sections}
					isPreparingReview={isPreparingReview}
					handleReviewModule={handleReviewModule}
					style={panelStyle}
				/>
			</Box>
		</ProethicaReportShell>
	);
};

export default ProethicaReportView;
